import { isLitePerf, isWebView, isLowEndDevice } from '@tasted/utils/perfTier';
import { canAfford3D, netTierDebug } from '@tasted/utils/netTier';

// Every model ships with the game, under public/models, and is served by the
// game's own host. Vercel Blob paused this account's store on 18 Oct 2026 (Hobby
// limit), and Supabase storage is on the free plan, so neither carries them.
// A new model gets a new filename so cached copies never go stale.
const BOTTLE_BASE_URL = '/models/';

const BOTTLE_MODELS = Object.freeze({
  '7caf3860-23d4-4796-a9a6-b1b2e2354fd6': `${BOTTLE_BASE_URL}ayama-56a60d27.glb`,
  '78e138ef-266c-4217-817d-84fd72000f87': `${BOTTLE_BASE_URL}journeys-end-c465a987.glb`,
  '0fcf421f-0138-4dc7-b3b8-ffc8720a6a4c': `${BOTTLE_BASE_URL}chamonix-1ff238fb.glb`,
  '1a3527b6-717d-461a-aed2-796dec1ad953': `${BOTTLE_BASE_URL}blue-canary-59a56b35.glb`,
  'ac045e4f-f869-44af-b726-7b11b3fb298b': `${BOTTLE_BASE_URL}leeuwenkuil-b8ad02f1.glb`,
  '95ba9e7b-86a5-486e-ab7c-cc3d6644bb0a': `${BOTTLE_BASE_URL}viento-48e61cc8.glb`,
  'afa71932-72cf-4bf4-aa4c-8553a0562fcc': `${BOTTLE_BASE_URL}yellowwood-aspoestertjie-2024-df385c71.glb`,
});

// Two separate questions, and a no from either one is a no (TAS-398):
//   can this DEVICE draw it   — isLitePerf
//   can this CONNECTION fetch it — canAfford3D, including the model and lighting
// Returning null here is all it takes: RevealBottle falls back to the photo
// (with hold-to-zoom) and TastingNotesSheet to its gradient, both tested paths.
export function bottleModelFor(wineId) {
  if (isLitePerf() || !canAfford3D()) return null;
  return typeof wineId === 'string' && Object.hasOwn(BOTTLE_MODELS, wineId)
    ? BOTTLE_MODELS[wineId]
    : null;
}

export const getBottleModelUrl = bottleModelFor;

/**
 * The same decision as bottleModelFor, with the reason attached, so the reveal
 * can say WHY a phone got the photograph (prod 2476, 2026-10-02: nothing
 * recorded what the phones had measured).
 */
export function bottleGate(wineId) {
  const hasModel = typeof wineId === 'string' && Object.hasOwn(BOTTLE_MODELS, wineId);
  if (!hasModel) return { modelUrl: null, reason: 'no-model' };
  if (isLitePerf()) {
    const reason = isWebView() ? 'webview' : isLowEndDevice() ? 'low-end-device' : 'lite-override';
    return { modelUrl: null, reason };
  }
  if (!canAfford3D()) return { modelUrl: null, reason: 'net-tier' };
  return { modelUrl: BOTTLE_MODELS[wineId], reason: null };
}

/** What the two gates saw on this device, PII-free, for analytics and /check. */
export function deviceTelemetry() {
  const n = typeof navigator !== 'undefined' ? navigator : {};
  const net = netTierDebug();
  return {
    device_memory: typeof n.deviceMemory === 'number' ? n.deviceMemory : null,
    hardware_concurrency: typeof n.hardwareConcurrency === 'number' ? n.hardwareConcurrency : null,
    webview: isWebView(),
    lite_perf: isLitePerf(),
    net_tier: net.tier,
    net_measured: net.measured,
    net_kbps: net.kbps,
    net_samples: net.samples,
    net_big_samples: net.bigSamples,
    net_hint: net.hint,
    net_cap: net.sessionCap,
    net_override: net.override,
  };
}
